import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Button } from "@ui";
import type { AnalysisResult, AnalyzedLocation } from "../lib/types";
import { SECTION_TITLES } from "../lib/theme";
import { SectionTabs, TAB_ICONS, type SectionTabItem } from "./section-tabs";
import { SectionHeader } from "./section-header";
import { SectionFundamentals } from "./section-fundamentals";
import { SectionOffer } from "./section-offer";
import { SectionEquipments } from "./section-equipments";
import { SectionInterdependances } from "./section-interdependances";
import { SectionGaps } from "./section-gaps";
import { SectionIdeas } from "./section-ideas";
import { CtaCard } from "./cta-card";

interface Props {
  result: AnalysisResult;
  location?: AnalyzedLocation;
  onReset: () => void;
}

interface SectionSlide {
  key: string;
  // Short pill label used by the carousel tab nav (e.g. "Manques").
  tab: SectionTabItem;
  // Full LLM-generated title used inside the slide as the section
  // header (e.g. "Analyse des activités qui manquent sur la zone
  // par rapport aux zones comparables"). Kept verbose because the
  // page itself can accommodate the length — only the tab pills can't.
  headerTitre: string;
  body: React.ReactNode;
}

// `onReset` stays on the Props contract for callers that still pass it
// (analyse-page.tsx wires it from its `reset` handler), but the result
// view itself no longer renders a "Nouvelle analyse" button — PR #399
// removed that UX in favor of scanning another QR or hitting the inSiti
// CTA. The prop is accepted-and-ignored on purpose; underscoring it
// silences the unused-binding check without breaking call sites.
export function AnalysisResultView({ result, location, onReset: _onReset }: Props) {
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(
      () => ref.current?.scrollIntoView({ behavior: "smooth" }),
      80,
    );
    return () => clearTimeout(id);
  }, []);

  // Each slide carries its own colorIndex so palette mapping is explicit
  // regardless of how many slides are present (section3b is optional).
  const slides: SectionSlide[] = [
    // Tab nav uses short 1-2 word labels (SECTION_TITLES) so the
    // carousel pills stay compact and don't overflow on mobile. The
    // full LLM-generated `titre` is preserved in `headerTitre` and
    // rendered inside each slide by SectionHeader so the page itself
    // remains expressive.
    { key: "s1",  tab: { titre: SECTION_TITLES[0], colorIndex: 0, Icon: TAB_ICONS[0] }, headerTitre: result.section1.titre, body: <SectionFundamentals  data={result.section1} /> },
    { key: "s2",  tab: { titre: SECTION_TITLES[1], colorIndex: 1, Icon: TAB_ICONS[1] }, headerTitre: result.section2.titre, body: <SectionOffer          data={result.section2} /> },
    { key: "s3",  tab: { titre: SECTION_TITLES[2], colorIndex: 2, Icon: TAB_ICONS[2] }, headerTitre: result.section3.titre, body: <SectionEquipments     data={result.section3} /> },
    ...(result.section3b ? [{ key: "s3b", tab: { titre: SECTION_TITLES[3], colorIndex: 3, Icon: TAB_ICONS[3] }, headerTitre: result.section3b.titre, body: <SectionInterdependances data={result.section3b} /> }] : []),
    { key: "s4",  tab: { titre: SECTION_TITLES[4], colorIndex: 4, Icon: TAB_ICONS[4] }, headerTitre: result.section4.titre, body: <SectionGaps           data={result.section4} /> },
    { key: "s5",  tab: { titre: SECTION_TITLES[5], colorIndex: 5, Icon: TAB_ICONS[5] }, headerTitre: result.section5.titre, body: <SectionIdeas          data={result.section5} /> },
  ];

  return (
    <div ref={ref} className="mt-7" data-testid="analysis-result">
      {location?.formatted_address && (
        <div className="mb-3 flex items-center gap-1.5 text-sm text-slate-500">
          <MapPin size={13} strokeWidth={2} className="shrink-0" />
          <span className="truncate">{location.formatted_address}</span>
        </div>
      )}

      <SectionTabs sections={slides.map((s) => s.tab)} active={active} onChange={setActive} />

      {/*
        react-responsive-carousel:
        - selectedItem / onChange make it a controlled component synced
          with `active` — tab clicks and swipes both flow through the
          same state.
        - swipeable + emulateTouch enables finger-follow drag on mobile
          AND mouse-drag on desktop (single source of gesture truth).
        - All built-in chrome (thumbs, indicators, arrows, status) hidden
          because SectionTabs already plays that role.
        - dynamicHeight: section heights vary widely (donut chart vs
          short list); the carousel resizes to the active slide.
        - preventMovementUntilSwipeScrollTolerance + 45px tolerance: the
          finger has to commit before the panel starts to follow, so
          vertical scrolling isn't accidentally hijacked.
      */}
      <Carousel
        selectedItem={active}
        onChange={(idx) => setActive(idx)}
        swipeable
        emulateTouch
        showThumbs={false}
        showStatus={false}
        showIndicators={false}
        showArrows={false}
        axis="horizontal"
        swipeScrollTolerance={45}
        preventMovementUntilSwipeScrollTolerance
        transitionTime={250}
        dynamicHeight
        ariaLabel="Sections de l'analyse"
      >
        {slides.map((slide, i) => (
          <div key={slide.key} data-testid={`section-${i}`} className="text-left">
            <SectionHeader
              colorIndex={slide.tab.colorIndex}
              Icon={slide.tab.Icon}
              titre={slide.headerTitre}
            />
            {slide.body}
          </div>
        ))}
      </Carousel>

      <div className="mt-5 flex gap-2">
        <Button
          variant="white"
          size="lg"
          icon={ChevronLeft}
          disabled={active === 0}
          onClick={() => setActive((i) => Math.max(0, i - 1))}
          className="flex-1 justify-center"
        >
          Précédent
        </Button>
        {/* Hand-built sibling to <Button> because the @ui Button only
            supports a left icon, and "Suivant" reads better with the
            chevron on the right. Sizing matches Button size="lg"
            (px-4 py-3 text-base) so the row stays balanced. */}
        <button
          type="button"
          disabled={active === slides.length - 1}
          onClick={() => setActive((i) => Math.min(slides.length - 1, i + 1))}
          className={
            "inline-flex flex-1 items-center justify-center gap-2 rounded-md " +
            "px-4 py-3 text-base font-medium bg-white text-gray-900 ring-1 " +
            "ring-inset ring-gray-300 hover:bg-gray-50 shadow-xs " +
            "disabled:opacity-50 disabled:cursor-not-allowed"
          }
        >
          Suivant
          <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      <CtaCard />
    </div>
  );
}
